import { Group, Paper, Skeleton as MantineSkeleton, Stack } from '@mantine/core';

/**
 * Skeleton pattern components for loading states.
 *
 * Loading contract (apply consistently across the app):
 * - Use Skeleton variants for INITIAL PAGE LOAD when content shape is known.
 * - Use Mantine `<Loader />` spinner for in-progress ACTIONS (saves, deletes, mutations).
 * - Never show a blank white area during loading — always one of these two patterns.
 *
 * All variants wrap Mantine's `<Skeleton>` and default `animate` to true.
 *
 * Shell: any.
 */

/** Props for {@link SkeletonRow}. */
export interface SkeletonRowProps {
  /** Number of cell blocks to render side-by-side. Default 5. */
  cols?: number;
  /** Base block height in px. Default 14. */
  height?: number;
  /** Whether the pulse animation runs. Default true. */
  animate?: boolean;
}

/**
 * SkeletonRow — horizontal row of `cols` skeleton blocks simulating a
 * DataTable row. Widths follow a [2.5, 1, 1, 1, 0.5] flex ratio so the row
 * mimics a label-leading record. Extra cols beyond 5 reuse the trailing
 * ratio.
 */
export function SkeletonRow({ cols = 5, height = 14, animate = true }: SkeletonRowProps) {
  const baseFlex = [2.5, 1, 1, 1, 0.5];
  return (
    <Group gap="md" wrap="nowrap" w="100%">
      {Array.from({ length: cols }).map((_, i) => {
        const flex = baseFlex[i] ?? baseFlex[baseFlex.length - 1] ?? 1;
        return (
          <MantineSkeleton key={i} height={height} animate={animate} radius="sm" style={{ flex }} />
        );
      })}
    </Group>
  );
}

/** Props for {@link SkeletonCard}. */
export interface SkeletonCardProps {
  /** Whether the pulse animation runs. Default true. */
  animate?: boolean;
}

/**
 * SkeletonCard — simulates a stat tile / info card with a tall image area
 * (~80px) and two text lines below.
 */
export function SkeletonCard({ animate = true }: SkeletonCardProps) {
  return (
    <Paper p="md" withBorder>
      <Stack gap="sm">
        <MantineSkeleton height={80} animate={animate} radius="sm" />
        <MantineSkeleton height={12} animate={animate} radius="sm" width="70%" />
        <MantineSkeleton height={10} animate={animate} radius="sm" width="45%" />
      </Stack>
    </Paper>
  );
}

/** Props for {@link SkeletonTile}. */
export interface SkeletonTileProps {
  /** Whether the pulse animation runs. Default true. */
  animate?: boolean;
}

/**
 * SkeletonTile — simulates a StatTile: small avatar circle plus two short
 * stacked lines beside it. Wrapped in a Paper for visual parity with the
 * real StatTile primitive.
 */
export function SkeletonTile({ animate = true }: SkeletonTileProps) {
  return (
    <Paper p="md" withBorder>
      <Group gap="md" wrap="nowrap">
        <MantineSkeleton height={36} circle animate={animate} />
        <Stack gap={6} style={{ flex: 1 }}>
          <MantineSkeleton height={10} animate={animate} radius="sm" width="50%" />
          <MantineSkeleton height={18} animate={animate} radius="sm" width="75%" />
        </Stack>
      </Group>
    </Paper>
  );
}

/** Props for {@link SkeletonStat}. */
export interface SkeletonStatProps {
  /** Whether the pulse animation runs. Default true. */
  animate?: boolean;
}

/**
 * SkeletonStat — simulates the bare label/value/sub shape a money or KPI
 * figure renders as while loading (a thin label bar, a tall value bar, a
 * thin sub-caption bar), stacked inside a bordered card. Distinct from
 * {@link SkeletonTile}, which simulates a leading-icon list tile, not a
 * standalone metric. One of the three loading shapes named by issue #274
 * ("skeletons shaped like the thing they replace" — stat / chart / list; see
 * {@link SkeletonChart} for the second and {@link SkeletonRow} composed in a
 * loop for the third).
 */
export function SkeletonStat({ animate = true }: SkeletonStatProps) {
  return (
    <Paper p="md" withBorder>
      <Stack gap={8}>
        <MantineSkeleton height={9} animate={animate} radius="sm" width="45%" />
        <MantineSkeleton height={20} animate={animate} radius="sm" width="70%" />
        <MantineSkeleton height={8} animate={animate} radius="sm" width="35%" />
      </Stack>
    </Paper>
  );
}

/** Props for {@link SkeletonChart}. */
export interface SkeletonChartProps {
  /** Whether the pulse animation runs. Default true. */
  animate?: boolean;
}

/**
 * SkeletonChart — simulates a chart/sparkline card: a label bar, a large
 * headline-figure bar, then a tall block standing in for the chart body.
 * The third of the three loading shapes named by issue #274 — see
 * {@link SkeletonStat}.
 */
export function SkeletonChart({ animate = true }: SkeletonChartProps) {
  return (
    <Paper p="md" withBorder>
      <Stack gap={10}>
        <MantineSkeleton height={9} animate={animate} radius="sm" width="30%" />
        <MantineSkeleton height={28} animate={animate} radius="sm" width="55%" />
        <MantineSkeleton height={64} animate={animate} radius="sm" />
      </Stack>
    </Paper>
  );
}

/** Props for {@link SkeletonTree}. */
export interface SkeletonTreeProps {
  /** Number of indented rows. Default 4. */
  levels?: number;
  /** Whether the pulse animation runs. Default true. */
  animate?: boolean;
}

/**
 * SkeletonTree — simulates a hierarchical tree (e.g. provenance, audit
 * roll-up) with `levels` rows each indented `level * 24px` further than
 * the previous one.
 */
export function SkeletonTree({ levels = 4, animate = true }: SkeletonTreeProps) {
  return (
    <Stack gap="xs" w="100%">
      {Array.from({ length: levels }).map((_, i) => (
        <Group key={i} gap="sm" wrap="nowrap" style={{ paddingLeft: i * 24 }}>
          <MantineSkeleton height={10} circle animate={animate} width={10} />
          <MantineSkeleton
            height={12}
            animate={animate}
            radius="sm"
            style={{ flex: 1, maxWidth: `${260 - i * 32}px` }}
          />
        </Group>
      ))}
    </Stack>
  );
}
